import { Fragment, useState, useEffect } from "react";
import { NavigateFunction } from "react-router-dom";
import { Typography, TabsContent } from "@altimateai/lego";
import { Loader } from "../../components/Loader";
import { TestCase, CreateTestCaseRequest } from "../../services/evals";
import { useTeamOptions } from "../hooks/useTeamOptions";
import { useAppState } from "../../hooks/useAppState";
import { TagsFilter } from "./TagsFilter";
import { TestCaseTableRow } from "./TestCaseTableRow";
import { TestCaseRowViewMode } from "./TestCaseRowViewMode";
import { TestCaseRowEditMode } from "./TestCaseRowEditMode";

interface TestCasesTabProps {
  testCases: TestCase[] | undefined;
  testCasesLoading: boolean;
  allTags: string[];
  filteredTestCases: TestCase[];
  selectedTestCaseIds: string[];
  selectedTags: string[];
  currentTenant: string;
  runningTestId: string | null;
  handleSelectAll: () => void;
  handleDeselectAll: () => void;
  handleToggleTag: (tag: string) => void;
  setSelectedTags: (tags: string[]) => void;
  handleToggleTestCase: (id: string) => void;
  handleRunEval: (id: string) => void;
  deleteMutation: {
    mutate: (id: string) => void;
    isPending: boolean;
  };
  updateMutation: {
    mutate: (params: { id: string; data: CreateTestCaseRequest }) => void;
    isPending: boolean;
  };
  expandedTestCaseId: string | null;
  setExpandedTestCaseId: (id: string | null) => void;
  navigate: NavigateFunction;
  criteria?: Array<{ id: string; name: string; description: string }>;
}

export const TestCasesTab: React.FC<TestCasesTabProps> = ({
  testCases,
  testCasesLoading,
  allTags,
  filteredTestCases,
  selectedTestCaseIds,
  selectedTags,
  currentTenant,
  runningTestId,
  handleSelectAll,
  handleDeselectAll,
  handleToggleTag,
  setSelectedTags,
  handleToggleTestCase,
  handleRunEval,
  deleteMutation,
  updateMutation,
  expandedTestCaseId,
  setExpandedTestCaseId,
  navigate,
  criteria,
}) => {
  const [editingTestCaseId, setEditingTestCaseId] = useState<string | null>(null);
  const [editingTestCaseData, setEditingTestCaseData] = useState<CreateTestCaseRequest | null>(
    null
  );
  const [wasSaving, setWasSaving] = useState(false);

  const { rbacEnabled } = useAppState();
  const { teamOptions, teamsLoading } = useTeamOptions();

  // Close edit mode after mutation completes
  useEffect(() => {
    if (wasSaving && !updateMutation.isPending) {
      // Mutation completed (success or error)
      setEditingTestCaseId(null);
      setEditingTestCaseData(null);
      setWasSaving(false);
    }
  }, [updateMutation.isPending, wasSaving]);

  return (
    <TabsContent value="test-cases">
      {testCasesLoading ? (
        <Loader label="Loading test cases..." />
      ) : (
        <>
          <TagsFilter
            allTags={allTags}
            selectedTags={selectedTags}
            testCases={testCases}
            onToggleTag={handleToggleTag}
            setSelectedTags={setSelectedTags}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
          />

          <div
            key="test-cases-tab"
            className="al-w-full al-overflow-x-auto al-border al-border-gray-200 al-rounded-md"
          >
            <table className="al-w-full al-divide-y al-divide-gray-200 al-table-fixed">
              <colgroup>
                <col className="al-w-12" />
                <col className="al-w-[25%]" />
                <col className="al-w-[20%]" />
                <col className="al-w-16" />
                <col className="al-w-[15%]" />
                <col className="al-w-[15%]" />
                <col className="al-w-[15%]" />
              </colgroup>
              <thead className="al-bg-gray-50">
                <tr>
                  <th className="al-px-4 al-py-3 al-text-left al-text-[13px] al-font-normal al-text-gray-500">
                    <input
                      type="checkbox"
                      checked={
                        filteredTestCases.length > 0 &&
                        filteredTestCases.every(tc => selectedTestCaseIds.includes(tc.id))
                      }
                      onChange={e => {
                        if (e.target.checked) {
                          handleSelectAll();
                        } else {
                          handleDeselectAll();
                        }
                      }}
                      className="al-w-4 al-h-4 al-text-blue-600 al-rounded al-cursor-pointer"
                    />
                  </th>
                  <th className="al-px-4 al-py-3 al-text-left">
                    <Typography size="xs" className="al-text-gray-500">
                      Name
                    </Typography>
                  </th>
                  <th className="al-px-4 al-py-3 al-text-left">
                    <Typography size="xs" className="al-text-gray-500">
                      Tags
                    </Typography>
                  </th>
                  <th className="al-px-4 al-py-3 al-text-left">
                    <Typography size="xs" className="al-text-gray-500">
                      Turns
                    </Typography>
                  </th>
                  <th className="al-px-4 al-py-3 al-text-left">
                    <Typography size="xs" className="al-text-gray-500">
                      Updated
                    </Typography>
                  </th>
                  <th className="al-px-4 al-py-3 al-text-left">
                    <Typography size="xs" className="al-text-gray-500">
                      Updated By
                    </Typography>
                  </th>
                  <th className="al-px-4 al-py-3 al-text-left">
                    <Typography size="xs" className="al-text-gray-500">
                      Actions
                    </Typography>
                  </th>
                </tr>
              </thead>
              <tbody className="al-bg-white al-divide-y al-divide-gray-200">
                {filteredTestCases.map(tc => {
                  const isSelected = selectedTestCaseIds.includes(tc.id);
                  const isExpanded = expandedTestCaseId === tc.id;
                  const isEditing = editingTestCaseId === tc.id;

                  return (
                    <Fragment key={tc.id}>
                      <TestCaseTableRow
                        testCase={tc}
                        isSelected={isSelected}
                        isExpanded={isExpanded}
                        isEditing={isEditing}
                        runningTestId={runningTestId}
                        onToggleSelect={() => handleToggleTestCase(tc.id)}
                        onToggleExpand={() => {
                          const newExpandedId = isExpanded ? null : tc.id;
                          setExpandedTestCaseId(newExpandedId);
                        }}
                        onEdit={data => {
                          if (data === null) {
                            setEditingTestCaseId(null);
                            setEditingTestCaseData(null);
                          } else {
                            setEditingTestCaseId(tc.id);
                            setEditingTestCaseData(data);
                          }
                        }}
                        onRun={() => handleRunEval(tc.id)}
                        onDelete={() => deleteMutation.mutate(tc.id)}
                        navigate={navigate}
                      />
                      {isExpanded && (
                        <tr>
                          <td colSpan={7} className="al-p-0">
                            <div className="al-bg-gray-50 al-border-t al-border-b al-border-gray-200">
                              <div className="al-p-6">
                                {isEditing && editingTestCaseData ? (
                                  <TestCaseRowEditMode
                                    editingTestCaseData={editingTestCaseData}
                                    setEditingTestCaseData={setEditingTestCaseData}
                                    currentTenant={currentTenant}
                                    rbacEnabled={rbacEnabled ?? false}
                                    teamOptions={teamOptions}
                                    teamsLoading={teamsLoading}
                                    criteria={criteria}
                                    onSave={() => {
                                      setWasSaving(true);
                                      updateMutation.mutate({
                                        id: tc.id,
                                        data: editingTestCaseData!,
                                      });
                                    }}
                                    onCancel={() => {
                                      setEditingTestCaseId(null);
                                      setEditingTestCaseData(null);
                                    }}
                                    isSaving={updateMutation.isPending}
                                  />
                                ) : (
                                  <TestCaseRowViewMode testCase={tc} />
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredTestCases.length === 0 && (
            <div className="al-text-center al-py-8">
              <Typography size="sm" className="al-text-gray-500">
                No test cases found
              </Typography>
            </div>
          )}
        </>
      )}
    </TabsContent>
  );
};
